import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Upload, Download, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { fmtNum, CURRENCIES, DRY_SIZES, REEFER_SIZES, SIZE_LABELS, sizesFor, CONTAINER_TYPES } from "@/lib/constants";

const empty = {
  shipping_line_id: "", shipment_type: "export", pol_id: "", pod_id: "",
  container_type: "dry", container_size: "20GP", ocean_freight: "", currency: "USD",
  surcharges: [],
  routing: "Direct", via_port: "", vessel_name: "", etd: "", eta: "", transit_time: "", frequency: "Weekly",
  valid_from: "", valid_until: "", free_time: "", remarks: "", active: true,
};

export default function RatesAdmin() {
  const [rates, setRates] = useState([]);
  const [lines, setLines] = useState([]);
  const [ports, setPorts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filterLine, setFilterLine] = useState("all");

  const load = () => api.get("/rates").then(({ data }) => setRates(data));
  useEffect(() => {
    load();
    api.get("/shipping-lines").then(({ data }) => setLines(data));
    api.get("/ports").then(({ data }) => setPorts(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ ...empty, ...r, surcharges: r.surcharges || [], valid_from: (r.valid_from || "").slice(0, 10), valid_until: (r.valid_until || "").slice(0, 10) });
    setEditId(r.id); setOpen(true);
  };

  const setSur = (i, k, v) => setForm((f) => ({ ...f, surcharges: f.surcharges.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addSur = () => setForm((f) => ({ ...f, surcharges: [...(f.surcharges || []), { name: "", amount: "", currency: f.currency || "USD" }] }));
  const delSur = (i) => setForm((f) => ({ ...f, surcharges: f.surcharges.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.shipping_line_id || !form.pol_id || !form.pod_id) { toast.error("Line, POL and POD are required"); return; }
    if (form.ocean_freight === "" || isNaN(Number(form.ocean_freight))) { toast.error("Ocean freight is required"); return; }
    const payload = {
      ...form,
      ocean_freight: Number(form.ocean_freight),
      transit_time: Number(form.transit_time) || 0,
      surcharges: (form.surcharges || []).filter((s) => s.name && s.amount !== "").map((s) => ({ name: s.name, amount: Number(s.amount) || 0, currency: s.currency || form.currency })),
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : new Date().toISOString(),
      valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : new Date(Date.now() + 365 * 864e5).toISOString(),
    };
    try {
      if (editId) await api.put(`/rates/${editId}`, payload);
      else await api.post("/rates", payload);
      toast.success(editId ? "Rate updated" : "Rate added");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => { await api.delete(`/rates/${id}`); toast.success("Rate deleted"); load(); };
  const dup = async (id) => { await api.post(`/rates/${id}/duplicate`); toast.success("Rate duplicated"); load(); };

  const download = async (path, name) => {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/rates/import/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} rates. ${data.error_count} row error(s).`);
      if (data.error_count && process.env.NODE_ENV === "development") console.warn("CSV errors:", data.errors);
      load();
    } catch { toast.error("Import failed"); }
    e.target.value = "";
  };

  const sizes = sizesFor(form.container_type);
  const shown = useMemo(() => filterLine === "all" ? rates : rates.filter((r) => r.shipping_line_id === filterLine), [rates, filterLine]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Select value={filterLine} onValueChange={setFilterLine}>
            <SelectTrigger className="h-9 w-52 rounded-sm" data-testid="rates-filter-line"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Lines ({rates.length})</SelectItem>{lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => download("/rates/import/template", "vk_rate_template.csv")} data-testid="csv-template-btn"><FileDown className="h-4 w-4 mr-1.5" />Template</Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => download("/rates/export/csv", "vk_rates.csv")} data-testid="csv-export-btn"><Download className="h-4 w-4 mr-1.5" />Export</Button>
          <label>
            <input type="file" accept=".csv" onChange={upload} className="hidden" data-testid="csv-upload-input" />
            <span className="inline-flex items-center h-9 px-3 text-sm border border-border rounded-sm cursor-pointer hover:bg-secondary"><Upload className="h-4 w-4 mr-1.5" />Bulk Upload</span>
          </label>
          <Button size="sm" className="bg-signal text-white rounded-sm" onClick={openNew} data-testid="add-rate-btn"><Plus className="h-4 w-4 mr-1.5" />Add Rate</Button>
        </div>
      </div>

      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-navy text-white text-xs uppercase">
              <th className="text-left px-3 py-2.5">Line</th>
              <th className="text-left px-3 py-2.5">Route</th>
              <th className="text-left px-3 py-2.5">Equip</th>
              <th className="text-right px-3 py-2.5">Freight</th>
              <th className="text-center px-3 py-2.5">Transit</th>
              <th className="text-left px-3 py-2.5">Valid Until</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/50" data-testid={`admin-rate-${r.id}`}>
                <td className="px-3 py-2.5 text-navy font-medium">{r.shipping_line_name}</td>
                <td className="px-3 py-2.5 font-mono-data text-xs text-muted-foreground capitalize">{r.shipment_type}: {r.pol_name}→{r.pod_name}</td>
                <td className="px-3 py-2.5 font-mono-data text-xs">{SIZE_LABELS[r.container_size]}</td>
                <td className="px-3 py-2.5 text-right font-mono-data">{r.currency} {fmtNum(r.ocean_freight)}</td>
                <td className="px-3 py-2.5 text-center font-mono-data">{r.transit_time}d</td>
                <td className="px-3 py-2.5 font-mono-data text-xs">{(r.valid_until || "").slice(0, 10)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => dup(r.id)} className="p-1.5 hover:bg-secondary rounded-sm" title="Duplicate" data-testid={`dup-rate-${r.id}`}><Copy className="h-3.5 w-3.5 text-muted-foreground" /></button>
                    <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-secondary rounded-sm" title="Edit" data-testid={`edit-rate-${r.id}`}><Pencil className="h-3.5 w-3.5 text-navy" /></button>
                    <button onClick={() => del(r.id)} className="p-1.5 hover:bg-secondary rounded-sm" title="Delete" data-testid={`del-rate-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Rate" : "Add Ocean Freight Rate"}</DialogTitle><DialogDescription className="sr-only">Ocean freight rate details form</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Shipping Line"><Sel value={form.shipping_line_id} onChange={(v) => set("shipping_line_id", v)} items={lines.map((l) => ({ v: l.id, l: l.name }))} testid="rf-line" /></F>
            <F label="Shipment Type"><Sel value={form.shipment_type} onChange={(v) => set("shipment_type", v)} items={[{ v: "export", l: "Export" }, { v: "import", l: "Import" }]} testid="rf-stype" /></F>
            <F label="POL (Port of Loading)"><Sel value={form.pol_id} onChange={(v) => set("pol_id", v)} items={ports.map((p) => ({ v: p.id, l: `${p.name}, ${p.country}` }))} testid="rf-pol" /></F>
            <F label="POD (Port of Discharge)"><Sel value={form.pod_id} onChange={(v) => set("pod_id", v)} items={ports.map((p) => ({ v: p.id, l: `${p.name}, ${p.country}` }))} testid="rf-pod" /></F>
            <F label="Container Type"><Sel value={form.container_type} onChange={(v) => { set("container_type", v); set("container_size", sizesFor(v)[0]); }} items={CONTAINER_TYPES} testid="rf-ctype" /></F>
            <F label="Container Size"><Sel value={form.container_size} onChange={(v) => set("container_size", v)} items={sizes.map((s) => ({ v: s, l: SIZE_LABELS[s] }))} testid="rf-size" /></F>
            <F label="Ocean Freight"><Input type="number" value={form.ocean_freight} onChange={(e) => set("ocean_freight", e.target.value)} className="rounded-sm font-mono-data" data-testid="rf-freight" /></F>
            <F label="Currency"><Sel value={form.currency} onChange={(v) => set("currency", v)} items={CURRENCIES.map((c) => ({ v: c, l: c }))} testid="rf-currency" /></F>
            <F label="Routing"><Sel value={form.routing} onChange={(v) => set("routing", v)} items={[{ v: "Direct", l: "Direct" }, { v: "Transshipment", l: "Transshipment" }]} testid="rf-routing" /></F>
            <F label="Transit (days)"><Input type="number" value={form.transit_time} onChange={(e) => set("transit_time", e.target.value)} className="rounded-sm font-mono-data" data-testid="rf-transit" /></F>
            <F label="Valid From"><Input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} className="rounded-sm font-mono-data" data-testid="rf-vfrom" /></F>
            <F label="Valid Until"><Input type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} className="rounded-sm font-mono-data" data-testid="rf-vuntil" /></F>
            <F label="Free Time"><Input value={form.free_time} onChange={(e) => set("free_time", e.target.value)} className="rounded-sm" data-testid="rf-freetime" /></F>
            <F label="Vessel Name"><Input value={form.vessel_name} onChange={(e) => set("vessel_name", e.target.value)} placeholder="e.g. MSC Diana" className="rounded-sm" data-testid="rf-vessel" /></F>
            <F label="Transshipment Port"><Input value={form.via_port} onChange={(e) => set("via_port", e.target.value)} placeholder="e.g. Colombo (blank = Direct)" className="rounded-sm" data-testid="rf-via" /></F>
            <F label="ETD (Departure)"><Input type="date" value={form.etd} onChange={(e) => set("etd", e.target.value)} className="rounded-sm font-mono-data" data-testid="rf-etd" /></F>
            <F label="ETA (Arrival)"><Input type="date" value={form.eta} onChange={(e) => set("eta", e.target.value)} className="rounded-sm font-mono-data" data-testid="rf-eta" /></F>
            <F label="Remarks"><Input value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className="rounded-sm" data-testid="rf-remarks" /></F>
          </div>

          <div className="mt-5 border-t border-border pt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold text-navy">Freight Surcharges</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-sm h-8" onClick={addSur} data-testid="rf-add-surcharge">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Surcharge
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Optional line items added on top of base ocean freight (e.g. BAF, CAF, PSS, ISPS). Included in the estimated total.</p>
            {(form.surcharges || []).length === 0 && <p className="text-xs text-muted-foreground italic">No surcharges added.</p>}
            <div className="space-y-2">
              {(form.surcharges || []).map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_100px_90px_auto] gap-2 items-center" data-testid={`rf-surcharge-${i}`}>
                  <Input value={s.name} onChange={(e) => setSur(i, "name", e.target.value)} placeholder="Surcharge name (e.g. BAF)" className="rounded-sm h-9" data-testid={`rf-surcharge-name-${i}`} />
                  <Input type="number" value={s.amount} onChange={(e) => setSur(i, "amount", e.target.value)} placeholder="Amount" className="rounded-sm h-9 font-mono-data" data-testid={`rf-surcharge-amount-${i}`} />
                  <Select value={s.currency} onValueChange={(v) => setSur(i, "currency", v)}>
                    <SelectTrigger className="rounded-sm h-9" data-testid={`rf-surcharge-cur-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  <button type="button" onClick={() => delSur(i)} className="p-2 hover:bg-secondary rounded-sm" data-testid={`rf-surcharge-del-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-signal text-white rounded-sm" onClick={save} data-testid="rf-save">Save Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const F = ({ label, children }) => (
  <div><Label className="text-xs mb-1.5 block text-muted-foreground">{label}</Label>{children}</div>
);
export const Sel = ({ value, onChange, items, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm" data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
    <SelectContent>{items.map((i) => <SelectItem key={i.v} value={i.v}>{i.l}</SelectItem>)}</SelectContent>
  </Select>
);
